import React, { useCallback, useEffect, useState } from 'react';
import { fetchShiftsList, deleteShift, fetchPortsList } from './services/api';
import { Shift, Port } from './types';
import { useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
// import 'react-loading-skeleton/dist/skeleton.css';
import { Button, Form } from 'react-bootstrap';
import { useAuth } from './AuthContext';
import moment from 'moment';

const ShiftList: React.FC = () => {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [ports, setPorts] = useState<Port[]>([]);
  const [selectedPort, setSelectedPort] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const navigate = useNavigate();
  const { token } = useAuth();

  const fetchShifts = useCallback(async (portId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchShiftsList();
      const shifts = Array.isArray(response.data) ? response.data : [];

      // Find the selected port to get its name
      const selectedPortObj = ports.find(p => String(p.id) === portId);

      // Filter shifts and add port name if missing
      const filteredShifts = shifts
        .filter((s: Shift) => String(s.port) === portId)
        .map((s: Shift) => ({
          ...s,
          port_name: s.port_name || selectedPortObj?.name || 'Unknown Location'
        }));

      setShifts(filteredShifts);
    } catch (error) {
      console.error('Error fetching shifts:', error);
      setError('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  }, [ports]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const response = await fetchPortsList();
        const ports = Array.isArray(response.data) ? response.data : [];
        setPorts(ports);

        // if (token && token.designation?.user_type === 'ADMIN' && token.port) {
        //   const portId = String(token.port);
        //   setSelectedPort(portId);
        //   await fetchShifts(portId);
        // }
      } catch (error) {
        console.error('Error loading initial data:', error);
        setError('Failed to load initial data');
      }
    };

    loadInitialData();
  }, []);

  const handlePortChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const portId = e.target.value;
    setSelectedPort(portId);
    if (portId) {
      await fetchShifts(portId);
    } else {
      setShifts([]);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this shift?')) return;
    setDeletingId(id);
    try {
      await deleteShift(id);
      setShifts(ss => ss.filter(s => s.id !== id));
    } catch {
      alert('Failed to delete shift');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-4 p-md-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Shifts</h2>
        <Button variant="primary" onClick={() => navigate('/shifts/create')}>
          <i className="fas fa-plus me-2"></i> Create Shift
        </Button>
      </div>
      <Form.Group className="mb-4" style={{ maxWidth: 320 }}>
        <Form.Label>Filter by Location</Form.Label>
        <Form.Select value={selectedPort} onChange={handlePortChange} disabled={!!(token && token.designation?.user_type === 'ADMIN' && token.port)}>
          <option value="">Select Location</option>
          {ports.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </Form.Select>
      </Form.Group>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="table-responsive">
        <table className="table table-bordered align-middle">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th>Location</th>
              <th>Start Time</th>
              <th>End Time</th>
              <th>Duration (hrs)</th>
              <th style={{ width: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  <td><Skeleton /></td>
                  <td><Skeleton /></td>
                  <td><Skeleton width={60} /></td>
                  <td><Skeleton width={60} /></td>
                  <td><Skeleton width={40} /></td>
                  <td><Skeleton width={70} /></td>
                </tr>
              ))
            ) : shifts.length === 0 ? (
              <tr><td colSpan={6} className="text-center">{selectedPort ? 'No shifts found for this port.' : 'Select a port to view shifts.'}</td></tr>
            ) : (
              shifts.map(s => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td>{s.port_name}</td>
                  <td>{moment(s.start_time, "HH:mm:ss").format("hh:mm A")}</td>
                  <td>{moment(s.end_time, "HH:mm:ss").format("hh:mm A")}</td>
                  <td>{s.duration_hours}</td>
                  <td>
                    {/* <button className="btn btn-sm btn-outline-primary me-2" onClick={() => navigate(`/shifts/edit/${s.id}`)}>
                      <i className="fas fa-edit"></i>
                    </button> */}
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(s.id)} disabled={deletingId === s.id}>
                      {deletingId === s.id ? <span className="spinner-border spinner-border-sm" /> : <i className="fas fa-trash"></i>}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ShiftList; 